import type { Medication } from '@/types/data'

export interface MedicationOption {
  value: string
  /** Full flat label, used for the trigger display and as a search fallback */
  label: string
  /** Product-level title rendered as the group header */
  group: string
  /** Dose strength rendered as a pill, e.g. "5mg" */
  strength: string | null
  /** Main option text, e.g. "28 Tablets" */
  detail: string
  /** Right-aligned supply duration, e.g. "1 month supply" */
  supply: string | null
  /** Recurring catalog cadence ("Monthly", "Every 2 months", ...), rendered as a per-row chip;
   *  null for one-offs — a one-time purchase is signalled by the absence of a chip, never a label */
  frequency: string | null
  /** Lower-cased haystack for "search by name or strength" */
  searchText: string
  /** V1 (Zoho) rows carry no stock column, so they are always treated as available */
  isInStock: boolean
}

export interface MedicationOptionGroup {
  title: string
  options: MedicationOption[]
}

export const decodeEntities = (text: string) =>
  text
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/&gt;/g, '>')
    .replace(/&lt;/g, '<')
    .replace(/&amp;/g, '&')

export const collapseSpaces = (text: string) => text.replace(/\s+/g, ' ').trim()

const trimSeparators = (text: string) => text.replace(/^[\s,-]+|[\s,-]+$/g, '')

const normalize = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

/**
 * "One Time Purchase" is the frequency taxonomy's sentinel for "not a subscription": PHP's
 * is.php, Connect's one-time-purchase slug checks and the prescription-details filter all
 * collapse it with an absent frequency. One-offs therefore render unmarked — no chip, same as
 * plain products and every other one-time surface (med-request chip, previous-orders tag) —
 * and only real cadences become chips.
 */
const isOneTimeFrequency = (frequency: string | null | undefined): boolean =>
  !!frequency && frequency.trim().toLowerCase() === 'one time purchase'

/** a catalogue row with no cadence belongs to a product with no subscription variants at all */
export const isOneTimeOption = (frequency: string | null | undefined): boolean =>
  !frequency || isOneTimeFrequency(frequency)

/** the exact set PHP `order\is::from_subscription_by_order_number()` matches on; `#CHAIN` is
    excluded there too, as it labels the subscription parent rather than a dispensed order */
const SUBSCRIPTION_ORDER_MARKERS = ['#LINK#', '#SUB#', '#MPSORD#', '#MPSMED#']

const isSubscriptionOrderNumber = (orderNumber: string | null | undefined): boolean =>
  !!orderNumber && SUBSCRIPTION_ORDER_MARKERS.some(marker => orderNumber.includes(marker))

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** frequency words leave the variant text either way — cadences move onto the chip, one-time
    wording just drops (no chip is the signal); the one-time pattern is spelled out because
    source data misspells it ("Purchase", "Purase", ...) */
const stripFrequencyText = (text: string, frequency: string) => {
  const pattern = isOneTimeFrequency(frequency)
    ? /,?\s*one time pur\w*/gi
    : new RegExp(`,?\\s*${escapeRegExp(frequency.trim())}\\b`, 'gi')
  return trimSeparators(text.replace(pattern, ''))
}

// "1 Month Supply (28 Tablets)" / "12 Months (252 Tablets)"
const MONTHS_WRAPPING = /^(\d+)\s*months?(?:\s+supply)?\s*\((.+)\)$/i
// "28 Tablets (1 Month)" / "1 x 73ml (2 Months)"
const MONTHS_TRAILING_PAREN = /^(.+?)\s*\(\s*(\d+)\s*months?\s*\)$/i
// "6 Months Supply 210 Tablets"
const MONTHS_SUPPLY_LOOSE = /^(\d+)\s*months?\s+supply\s+(.+)$/i
// "3 X 60ml 3 Months" / "212 Capsules 6 Months"
const MONTHS_TRAILING = /^(.+?)\s+(\d+)\s*months?$/i

export const parseSupply = (raw: string): { detail: string; supply: string | null } => {
  const text = collapseSpaces(raw)

  const toSupply = (months: string) => `${months} month${months === '1' ? '' : 's'} supply`

  const wrapping = text.match(MONTHS_WRAPPING)
  if (wrapping) return { detail: trimSeparators(wrapping[2]), supply: toSupply(wrapping[1]) }

  const trailingParen = text.match(MONTHS_TRAILING_PAREN)
  if (trailingParen) return { detail: trimSeparators(trailingParen[1]), supply: toSupply(trailingParen[2]) }

  const loose = text.match(MONTHS_SUPPLY_LOOSE)
  if (loose) return { detail: trimSeparators(loose[2]), supply: toSupply(loose[1]) }

  const trailing = text.match(MONTHS_TRAILING)
  if (trailing) return { detail: trimSeparators(trailing[1]), supply: toSupply(trailing[2]) }

  return { detail: text, supply: null }
}

/** drop the pill text from the start of a fragment when it just repeats it */
const stripPillPrefix = (text: string, pill: string) => {
  if (normalize(text) === normalize(pill)) return ''
  if (text.toLowerCase().startsWith(pill.toLowerCase())) return trimSeparators(text.slice(pill.length))
  return text
}

/** whole-word containment on normalized text, so "128 tablets" does not swallow "28 tablets" */
const containsNormalized = (haystack: string, needle: string) =>
  ` ${normalize(haystack)} `.includes(` ${normalize(needle)} `)

/**
 * Brand is a first-class variant dimension (Connect asks "Which brand would you prefer?") and is
 * routinely the ONLY thing separating two variants of the same strength and quantity — e.g. the
 * orodispersible and plain-tablet forms of Zolmitriptan. It has to be rendered, because the synced
 * variation title cannot carry it: WooCommerce drops the attribute suffix from a variation's title
 * once a product has several attributes, so every variation of such a product arrives named after
 * the parent product.
 *
 * Only the part the title does not already say is returned — a leading run repeated verbatim from
 * the product title is dropped ("Zolmitriptan Orodispersible Tablets" under "Zolmitriptan Tablets
 * (2.5mg, 5mg)" → "Orodispersible Tablets"), and a brand the title already contains drops entirely.
 */
export const brandLabel = (brand: string | null | undefined, title: string): string => {
  const text = collapseSpaces(decodeEntities(brand ?? ''))
  if (!text || containsNormalized(title, text)) return ''

  const words = text.split(' ')
  const titleWords = collapseSpaces(title).split(' ')

  let shared = 0
  // never consume the last word: a brand reduced to nothing would be worse than a repetitive one
  while (
    shared < words.length - 1 &&
    normalize(words[shared]) !== '' &&
    normalize(words[shared]) === normalize(titleWords[shared] ?? '')
  ) {
    shared++
  }

  const trimmed = words.slice(shared).join(' ')
  // a purely numeric remainder ("Microgynon 30" under "Microgynon" → "30") reads as a pack size
  // sitting next to the real strength and quantity, so keep the brand whole in that case
  return /[a-z]/i.test(trimmed) ? trimmed : text
}

const buildOption = (
  medication: Medication,
  label: string,
  group: string,
  strength: string | null,
  body: string,
  rawQuantity: string,
  frequency: string | null
): MedicationOption => {
  const { detail: quantityDetail, supply } = parseSupply(rawQuantity)

  let pill = strength ? collapseSpaces(strength) : null
  let descriptor = pill ? stripPillPrefix(body, pill) : body

  // a descriptor that only repeats the quantity adds nothing — prefer the parsed form
  if (normalize(descriptor) === normalize(quantityDetail) || normalize(descriptor) === normalize(rawQuantity)) {
    descriptor = ''
  }

  // keep both fragments when each carries information the other lacks, e.g. "Pre-filled Pen · 1 Pen"
  let detail: string
  if (!descriptor) detail = quantityDetail
  else if (!quantityDetail || containsNormalized(descriptor, quantityDetail)) detail = descriptor
  else if (containsNormalized(quantityDetail, descriptor)) detail = quantityDetail
  else detail = `${descriptor} · ${quantityDetail}`

  if (!detail && pill) {
    detail = pill
    pill = null
  }
  // dash-less labels parse to an empty body — repeat the group text rather than render a bare "—" row
  if (!detail) detail = group || '—'

  return {
    value: medication.wooSKU,
    label,
    group,
    strength: pill,
    detail,
    supply,
    frequency: isOneTimeFrequency(frequency) ? null : frequency,
    // the raw label plus every rendered fragment, so anything visible (or stripped from view) stays
    // searchable; the normalized copy makes punctuation optional ("co codamol" finds "Co-codamol")
    searchText: [label, normalize(label), group, pill ?? '', detail, supply ?? '', frequency ?? '']
      .join(' ')
      .toLowerCase(),
    isInStock: medication.isInStock !== false
  }
}

export const toMedicationOption = (medication: Medication): MedicationOption => {
  const label = collapseSpaces(decodeEntities(medication.productName ?? ''))
  const frequency = medication.subscriptionFrequency

  const group = collapseSpaces(decodeEntities(medication.productTitle ?? ''))
  const variantTitle = collapseSpaces(decodeEntities(medication.variantTitle ?? ''))

  let descriptor = variantTitle.toLowerCase().startsWith(group.toLowerCase())
    ? variantTitle.slice(group.length)
    : variantTitle
  descriptor = trimSeparators(descriptor)
  if (frequency) descriptor = stripFrequencyText(descriptor, frequency)

  const brand = brandLabel(medication.brand, group)
  if (brand && !containsNormalized(descriptor, brand)) {
    descriptor = descriptor ? `${brand} · ${descriptor}` : brand
  }

  return buildOption(
    medication,
    label,
    group,
    medication.strength ?? null,
    descriptor,
    medication.quantity ?? '',
    frequency ?? null
  )
}

/** Product-level stand-in for an order item PHP stored no variation on; shows the bare product title. */
export const productLevelMedicationFor = (medications: Medication[], productHash: string): Medication | undefined => {
  if (!productHash) return undefined

  const variants = medications.filter(medication => medication.productHash === productHash)
  const first = variants[0]
  if (!first) return undefined

  return {
    id: productHash,
    wooSKU: productHash,
    variantHash: null,
    productHash,
    productName: first.productTitle || first.productName,
    type: first.type,
    medicationDirections: '',
    subscriptionFrequency: null,
    strength: null,
    quantity: null,
    productTitle: first.productTitle ?? null,
    variantTitle: null,
    brand: null,
    isInStock: variants.some(variant => variant.isInStock !== false),
    categoryHashes: first.categoryHashes
  }
}

/** The product family a medication belongs to — the exclusion key only for picks with no variation. */
const productKeyOf = (medication: Pick<Medication, 'productHash' | 'variantHash'>) =>
  medication.productHash || medication.variantHash || null

/**
 * Option values a card disables because sibling cards picked them. Compared by variation, like PHP's
 * duplicate guard; a pick with no variation excludes its whole product family.
 */
export const takenMedicationValues = (
  siblingPicks: readonly (Medication | null)[],
  medications: readonly Medication[]
): Set<string> => {
  const picks = siblingPicks.filter((pick): pick is Medication => !!pick)

  const takenVariants = new Set<string>()
  const ambiguousProducts = new Set<string>()

  for (const pick of picks) {
    if (pick.variantHash) {
      takenVariants.add(pick.variantHash)
      continue
    }
    const productKey = productKeyOf(pick)
    if (productKey) ambiguousProducts.add(productKey)
  }

  const isTaken = (medication: Medication) => {
    if (medication.variantHash && takenVariants.has(medication.variantHash)) return true
    const productKey = productKeyOf(medication)
    return !!productKey && ambiguousProducts.has(productKey)
  }

  return new Set(
    [
      // the picks themselves, in case one is missing from the catalogue
      ...picks.flatMap(pick => [pick.wooSKU, pick.variantHash]),
      ...medications.filter(isTaken).flatMap(medication => [medication.wooSKU, medication.variantHash])
    ].filter((value): value is string => !!value)
  )
}

/**
 * A subscription request may only be swapped for the same cadence, a one-time request for another
 * one-time item.
 *
 * An empty `requestFrequency` is ambiguous: NodeAPI collapses "the ordered item has no cadence"
 * and "the ordered item's variation row could not be joined at all" to the same `''`. Finding the
 * ordered item in the catalogue separates them — if it is missing, the cadence is unknown, and the
 * order number is then the only thing left that says which half of the catalogue applies.
 *
 * Returns a new array; the caller unshifts into it and the input is react-query cache data.
 */
export const filterByCadence = ({
  medications,
  requestFrequency,
  orderNumber,
  orderedItemResolved
}: {
  medications: Medication[]
  requestFrequency?: string | null
  orderNumber?: string | null
  orderedItemResolved: boolean
}): Medication[] => {
  if (requestFrequency && !isOneTimeFrequency(requestFrequency)) {
    const cadence = requestFrequency.toLowerCase()
    return medications.filter(medication => medication?.subscriptionFrequency?.toLowerCase() === cadence)
  }

  if (!requestFrequency && !orderedItemResolved && isSubscriptionOrderNumber(orderNumber)) {
    // any recurring option beats the one-time half here; never narrow to nothing, though, or the
    // request becomes unactionable
    const recurring = medications.filter(medication => !isOneTimeOption(medication?.subscriptionFrequency))
    if (recurring.length) return recurring
  }

  return medications.filter(medication => isOneTimeOption(medication?.subscriptionFrequency))
}

/**
 * The catalogue a prescriber may swap the ordered item for. Lives here rather than in the
 * component so the selectable set is testable on its own — it is the layer that decides which
 * clinical options exist, and it silently dropped real ones for as long as it keyed on the label.
 */
export const buildMedicationOptions = ({
  medications,
  orderedItemId,
  requestFrequency,
  orderNumber,
  ignoreCadence = false
}: {
  medications: Medication[]
  orderedItemId?: string | null
  requestFrequency?: string | null
  orderNumber?: string | null
  /** skip the cadence filter; MPS items are cadence-less rows the `#MPSMED#` marker would drop */
  ignoreCadence?: boolean
}): MedicationOption[] => {
  // `variantHash` is the identity of a variation and of a variation-less product alike (NodeAPI
  // serves `hash = variation hash ?? product hash`), so matching `productHash` as well only ever
  // hit med requests that could not resolve their variation — and resolved them to whichever
  // variation happened to come first, presenting that guess as the ordered item.
  // guarded on the id: V1 rows carry no `variantHash`, so an absent `orderedItemId` would match
  // `undefined === undefined` and resolve the ordered item to whichever row happened to come first
  const orderedItem = orderedItemId
    ? medications.find(medication => medication.variantHash === orderedItemId)
    : undefined

  const filtered = ignoreCadence
    ? [...medications]
    : filterByCadence({ medications, requestFrequency, orderNumber, orderedItemResolved: !!orderedItem })

  // the item actually ordered stays selectable even when the cadence filter would have dropped it
  if (orderedItem) filtered.unshift(orderedItem)

  // dedupe on identity, never on the label: distinct variants of a multi-attribute product share
  // a label whenever the fields we render happen to coincide, and dropping one of those hides a
  // real, prescribable option (e.g. Zolmitriptan's orodispersible and plain-tablet forms). The
  // only true duplicate here is the ordered item unshifted above when it also survived the filter.
  const seenHashes = new Set<string>()

  return filtered.reduce<MedicationOption[]>((options, medication) => {
    const dedupKey = medication.variantHash || medication.wooSKU
    if (seenHashes.has(dedupKey)) return options
    seenHashes.add(dedupKey)
    options.push(toMedicationOption(medication))
    return options
  }, [])
}

export const groupMedicationOptions = (options: MedicationOption[]): MedicationOptionGroup[] => {
  const groups = new Map<string, MedicationOptionGroup>()

  for (const option of options) {
    const key = normalize(option.group)
    const existing = groups.get(key)
    if (existing) existing.options.push(option)
    else groups.set(key, { title: option.group, options: [option] })
  }

  return [...groups.values()]
}
